import { connect, ConnectedProps } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom-v5-compat';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { LIST_TYPE_TO_LIST_ITEM_TYPE } from 'dashboard/lib/constants/list';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { List, ListAttributes } from 'dashboard/models/list';
import {
  EmailAddressMatchType,
  ListItem,
  ListItemBrowserFingerprint,
  ListItemCountry,
  ListItemDeviceFingerprint,
  ListItemEmailAddress,
  ListItemFace,
  ListItemField,
  ListItemGeolocation,
  ListItemGovernmentIdNumber,
  ListItemIpAddress,
  ListItemName,
  ListItemPhoneNumber,
  ListItemSelfieBackground,
  ListItemStatus,
  ListItemString,
} from 'dashboard/models/list-item';
import {
  clearList,
  saveList,
  saveListFilterParams,
  setCurrentPage,
  setPageSize,
  setSortParams,
} from 'dashboard/redux/actions/list';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { loadList } from 'dashboard/redux/thunk-actions/list';
import DashboardListsShowView from 'dashboard/views/DashboardListsShowView';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { CountryCode } from 'lib/constants/locales';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useApiLoader from 'lib/hooks/useApiLoader';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import useRedirectOnEnvironmentChange from 'lib/hooks/useRedirectOnEnvironmentChange';
import useTable from 'lib/hooks/useTable';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { Place } from 'lib/interfaces/place';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { assertNever } from 'lib/utils/types';

type DashboardListShowRouteProps = PropsFromRedux;

type Params = { listId: string };

const DashboardListShowRoute = (props: DashboardListShowRouteProps) => {
  const { listId } = useParams<Params>() as Params; // listId will always be defined
  const navigate = useNavigate();

  useOnMountAndUnmount(props.clearList);

  useRedirectOnEnvironmentChange('/dashboard/lists');

  const { loading, callback: refetchList } = useApiLoader({
    loader: () =>
      props.loadList({
        id: listId,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        filters: props.listFilterParams,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        currentPage: props.currentPage,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        pageSize: props.pageSize,
        currentEnvironment: props.currentEnvironment,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        sort: props.sortParams,
      }),
    deps: [
      props.loadList,
      listId,
      props.listFilterParams,
      props.currentPage,
      props.pageSize,
      props.currentEnvironment,
      props.sortParams,
    ],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('Failed to load list.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  const handleStatusFilterUpdate = (statuses: ListItemStatus[]) => {
    props.saveListFilterParams({
      ...props.listFilterParams,
      status: statuses,
    });
  };

  const handleDrawCircleComplete = async (place: Place) => {
    try {
      addListItem({
        latitude: place.lat,
        longitude: place.lng,
        radiusMeters: place.radiusMeters,
      });
    } catch (err) {
      // no-op
    }
  };

  const deleteList = async (listId: string) => {
    try {
      await SessionClientDashboard.deleteResource<List>(ApiEndpoint.List, listId);
      props.showMessage('Successfully archived list.', MessageType.Success, true);
      navigate('/dashboard/lists');
    } catch {
      props.showMessage('Failed to archive list, please try again.', MessageType.Failure);
    }
  };

  const updateList = async (list: List, attributes: ListAttributes) => {
    try {
      await SessionClientDashboard.updateResource<List>({
        type: list.type,
        id: list.id,
        attributes,
      });
      refetchList();
      props.showMessage('Updated list.', MessageType.Success, true);
    } catch {
      props.showMessage('Failed to update list, please try again.', MessageType.Failure);
    }
  };

  const addListItem = async (values: {
    listItemValue?: string;
    idClass?: GovernmentIdClass;
    idNumber?: string;
    nameFirst?: string;
    nameLast?: string;
    latitude?: number;
    longitude?: number;
    radiusMeters?: number;
    facePhoto?: File;
    selfiePhoto?: File;
    countryCode?: CountryCode;
    matchType?: EmailAddressMatchType;
    fieldValue?: string;
  }) => {
    try {
      if (!props.list) return;
      const listItemType = LIST_TYPE_TO_LIST_ITEM_TYPE[props.list.type];

      switch (listItemType) {
        case ApiModel.ListItemGovernmentIdNumber:
          await SessionClientDashboard.postResourceCreate<ListItemGovernmentIdNumber>({
            attributes: {
              'list-id': props.list.id,
              'id-class': values.idClass,
              'id-number': values.idNumber,
              'environment-id': props.currentEnvironment.id,
            },
            type: ApiModel.ListItemGovernmentIdNumber,
          });
          break;
        case ApiModel.ListItemIpAddress:
          await SessionClientDashboard.postResourceCreate<ListItemIpAddress>({
            attributes: {
              'list-id': props.list.id,
              value: values.listItemValue,
              'environment-id': props.currentEnvironment.id,
            },
            type: ApiModel.ListItemIpAddress,
          });
          break;
        case ApiModel.ListItemName:
          await SessionClientDashboard.postResourceCreate<ListItemName>({
            attributes: {
              'list-id': props.list.id,
              'name-first': values.nameFirst,
              'name-last': values.nameLast,
              'environment-id': props.currentEnvironment.id,
            },
            type: ApiModel.ListItemName,
          });
          break;
        case ApiModel.ListItemPhoneNumber:
          await SessionClientDashboard.postResourceCreate<ListItemPhoneNumber>({
            attributes: {
              'list-id': props.list.id,
              value: values.listItemValue,
              'environment-id': props.currentEnvironment.id,
            },
            type: ApiModel.ListItemPhoneNumber,
          });
          break;
        case ApiModel.ListItemEmailAddress:
          await SessionClientDashboard.postResourceCreate<ListItemEmailAddress>({
            attributes: {
              'list-id': props.list.id,
              'match-type': values.matchType,
              value: values.listItemValue,
              'environment-id': props.currentEnvironment.id,
            },
            type: ApiModel.ListItemEmailAddress,
          });
          break;
        case ApiModel.ListItemGeolocation:
          await SessionClientDashboard.postResourceCreate<ListItemGeolocation>({
            attributes: {
              'list-id': props.list.id,
              latitude: values.latitude,
              longitude: values.longitude,
              'radius-meters': values.radiusMeters,
              'environment-id': props.currentEnvironment.id,
            },
            type: ApiModel.ListItemGeolocation,
          });
          break;
        case ApiModel.ListItemBrowserFingerprint:
          await SessionClientDashboard.postResourceCreate<ListItemBrowserFingerprint>({
            attributes: {
              'list-id': props.list.id,
              value: values.listItemValue,
              'environment-id': props.currentEnvironment.id,
            },
            type: ApiModel.ListItemBrowserFingerprint,
          });
          break;
        case ApiModel.ListItemDeviceFingerprint:
          await SessionClientDashboard.postResourceCreate<ListItemDeviceFingerprint>({
            attributes: {
              'list-id': props.list.id,
              value: values.listItemValue,
              'environment-id': props.currentEnvironment.id,
            },
            type: ApiModel.ListItemDeviceFingerprint,
          });
          break;
        case ApiModel.ListItemFace:
          await SessionClientDashboard.postResourceCreate<ListItemFace>(
            {
              attributes: {
                'list-id': props.list.id,
                'face-photo-upload': values.facePhoto,
                'environment-id': props.currentEnvironment.id,
              },
              type: ApiModel.ListItemFace,
            },
            {
              contentType: 'multipart/form-data',
            },
          );
          break;
        case ApiModel.ListItemCountry:
          await SessionClientDashboard.postResourceCreate<ListItemCountry>({
            attributes: {
              'list-id': props.list.id,
              'country-code': values.countryCode,
              'environment-id': props.currentEnvironment.id,
            },
            type: ApiModel.ListItemCountry,
          });
          break;
        case ApiModel.ListItemField:
          await SessionClientDashboard.postResourceCreate<ListItemField>({
            attributes: {
              'list-id': props.list.id,
              'field-value': values.fieldValue,
              'environment-id': props.currentEnvironment.id,
            },
            type: ApiModel.ListItemField,
          });
          break;
        case ApiModel.ListItemString:
          await SessionClientDashboard.postResourceCreate<ListItemString>({
            attributes: {
              'list-id': props.list.id,
              value: values.listItemValue,
              'environment-id': props.currentEnvironment.id,
            },
            type: ApiModel.ListItemString,
          });
          break;
        case ApiModel.ListItemSelfieBackground:
          await SessionClientDashboard.postResourceCreate<ListItemSelfieBackground>(
            {
              attributes: {
                'list-id': props.list.id,
                'selfie-photo-upload': values.selfiePhoto,
                'environment-id': props.currentEnvironment.id,
              },
              type: ApiModel.ListItemSelfieBackground,
            },
            {
              contentType: 'multipart/form-data',
            },
          );
          break;
        default:
          assertNever(listItemType);
      }

      props.showMessage('Successfully added item to list.', MessageType.Success);
      refetchList();
    } catch (err) {
      // NOTE: we should be deliberate about checking error codes. See
      // https://www.notion.so/withpersona/Error-serialization-282f3c016ee7450eabe42dd273762cf0
      if (err instanceof ApiResponseError && err.status === 409) {
        props.showMessage('Item already exists in this list.', MessageType.Failure);
      } else {
        props.showMessage('Failed to add item to list, please try again.', MessageType.Failure);
      }

      throw err;
    }
  };

  const deleteListItem = async (listItemId?: string) => {
    try {
      if (!props.list || !listItemId) return;
      const listItemType = LIST_TYPE_TO_LIST_ITEM_TYPE[props.list.type];
      await SessionClientDashboard.deleteResourceWithPayload<ListItem>(
        { type: listItemType, attributes: { 'environment-id': props.currentEnvironment.id } },
        ApiEndpoint.ListItem,
        listItemId,
      );
      props.showMessage('Successfully archived item from list.', MessageType.Success);
      refetchList();
    } catch {
      props.showMessage('Failed to archive item from list, please try again.', MessageType.Failure);
    }
  };

  const { handlePageLinkClick, handlePageSizeClick, handleSortUpdate, handleOmniSearch } = useTable(
    {
      filters: props.listFilterParams,
      sortParams: props.sortParams,
      pageSize: props.pageSize,
      currentPage: props.currentPage,
      setFilters: props.saveListFilterParams,
      setCurrentPage: props.setCurrentPage,
      setPageSize: props.setPageSize,
      setSortParams: props.setSortParams,
    },
  );

  return (
    <DashboardListsShowView
      list={props.list}
      listItems={props.listItems}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      filters={props.listFilterParams}
      users={props.users}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      paginationParams={props.paginationParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pageSize={props.pageSize}
      isLoading={loading}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      sortParams={props.sortParams}
      addListItem={addListItem}
      deleteList={deleteList}
      deleteListItem={deleteListItem}
      updateList={updateList}
      handleStatusFilterUpdate={handleStatusFilterUpdate}
      handleDrawCircleComplete={handleDrawCircleComplete}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      handleOmniSearch={handleOmniSearch}
      handleSortUpdate={handleSortUpdate}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    list: state.list.list,
    listItems: state.list.listItems,
    users: state.list.users,
    listFilterParams: state.list.filters,
    sortParams: state.list.sortParams,
    paginationParams: state.list.paginationParams,
    currentPage: state.list.currentPage,
    pageSize: state.list.pageSize,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
  };
};

const mapDispatchToProps = {
  clearList,
  loadList,
  saveList,
  saveListFilterParams,
  setCurrentPage,
  setPageSize,
  setSortParams,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardListShowRoute);
